import { useEffect, useRef, useState } from 'react'

/**
 * A dropdown that looks like the rest of the app, not like the browser.
 *
 * A native `<select>`'s trigger can be themed; its open popup cannot — the
 * OS draws that part, which is why picking "All parties" used to open a
 * plain white system menu in the middle of an otherwise dark screen. This
 * renders both halves itself, following the same trigger-button-plus-popover
 * shape as `DateRangePicker`: click to open, click outside or Escape to
 * close, one option chosen at a time.
 */
export default function Select({ options, value, onChange, title }) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e) => rootRef.current && !rootRef.current.contains(e.target) && setOpen(false)
    const onKey = (e) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const current = options.find((o) => o.value === value) ?? options[0]

  return (
    <div className="msel" ref={rootRef}>
      <button
        type="button"
        className={`msel-trigger${value && value !== 'all' ? ' has-value' : ''}`}
        title={title}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{current?.label}</span>
        <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" className={`msel-caret${open ? ' up' : ''}`}>
          <path d="M6 9l6 6l6-6" />
        </svg>
      </button>

      {open && (
        <div className="msel-pop" role="listbox">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              className={`msel-opt${o.value === value ? ' on' : ''}`}
              onClick={() => {
                onChange(o.value)
                setOpen(false)
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
